import { Grid, Pagination, Box, Stack, Typography, Select, MenuItem, Divider } from "@mui/material";
import { useState, useMemo } from "react";
import PatientCard from "./PatientCard";
import { NursePatient } from "../models";

interface PatientCardViewProps {
    data: NursePatient[];
    loading?: boolean;
    onAction: (action: string, row: NursePatient) => void;
    t: (key: string) => string;
    isolationIconsMap: Record<number, string>;
    visitTypes?: Array<{ id: number; name: string }>;
    visitTypeOverrides?: Record<number, { id: number; name: string }>;
    onVisitTypeSaved?: (patientId: number, visitType: { id: number; name: string } | null) => void;
    onVisitTypeUpdating?: (updating: boolean) => void;
    syncScroll?: boolean;
}

const ITEMS_PER_PAGE_OPTIONS = [4, 8, 12, 16, 20];

export default function PatientCardView({ data, loading, onAction, t, isolationIconsMap, visitTypes, visitTypeOverrides, onVisitTypeSaved, onVisitTypeUpdating, syncScroll }: PatientCardViewProps) {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [activeTab, setActiveTab] = useState(0); // Global tab state for syncing

    const pageCount = Math.ceil(data.length / rowsPerPage);

    const currentData = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return data.slice(start, start + rowsPerPage);
    }, [data, page, rowsPerPage]);

    const handleTabChange = (newTab: number) => {
        if (syncScroll) {
            setActiveTab(newTab);
        }
    };

    const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleChangeRowsPerPage = (event: any) => {
        setRowsPerPage(Number(event.target.value));
        setPage(1);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <Typography>{t("loading") || "Yükleniyor..."}</Typography>
            </Box>
        );
    }

    if (data.length === 0) {
        return (
            <Box sx={{ width: "100%", p: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Typography variant="h6" color="text.secondary">
                    {t("common.no-record-found") || "Kayıt Bulunamadı"}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", p: 1, position: 'relative' }}>
            <Grid container spacing={2}>
                {currentData.map((row) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={row.id} sx={{ display: 'flex', minWidth: 0 }}>
                        <PatientCard
                            data={row}
                            onAction={onAction}
                            t={t}
                            isolationIconsMap={isolationIconsMap}
                            visitTypes={visitTypes}
                            arrivalReasonOverride={visitTypeOverrides?.[Number((row as any)?.ptid ?? row.id)] ?? null}
                            onVisitTypeSaved={onVisitTypeSaved}
                            onVisitTypeUpdating={onVisitTypeUpdating}
                            activeTabProp={syncScroll ? activeTab : undefined}
                            onTabChange={syncScroll ? handleTabChange : undefined}
                        />
                    </Grid>
                ))}
            </Grid>

            {data.length > 0 && (
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 2, sm: 2 }}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mt: 3 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {t("common.rows-per-page")}
                        </Typography>
                        <Select
                            size="small"
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                            sx={{ height: 32, minWidth: 60 }}
                        >
                            {ITEMS_PER_PAGE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>

                        <Divider orientation="vertical" flexItem sx={{ height: 16, my: 'auto', mx: 1, borderColor: 'divider' }} />

                        <Typography variant="caption" color="text.secondary">
                            {t("common.total-records")} <b>{data.length}</b>
                        </Typography>
                    </Stack>

                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handleChangePage}
                        color="primary"
                        shape="rounded"
                        siblingCount={0}
                        boundaryCount={1}
                    />
                </Stack>
            )}
        </Box>
    );
}
